import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { patchState, signalStore, type, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { setAllEntities, withEntities } from '@ngrx/signals/entities';
import { cloneDeep, differenceBy, isEmpty } from 'lodash';
import { firstValueFrom, Subscription } from 'rxjs';

import {
  AuthService,
  HandleErrorsService,
  NotificationService,
  PrintableDataService,
  PrintingService,
  TouchTranslateService,
  UserService,
} from 'app/app-services';
import { PrintingSessionService } from 'app/app-services/printing-session.service';
import { AVAILABLE_ZOOM } from 'app/constants/available-zoom.const';
import { Brand } from 'app/models/brand.model';
import { EntitiesModel } from 'app/models/entities.model';
import { FabricJSON } from 'app/models/fabric.model';
import { FilterFormFields } from 'app/models/filter-form';
import { Model } from 'app/models/model.model';
import { NotificationType } from 'app/models/notification-types.const';
import { Piece, PrintableData, PrintableDataSession } from 'app/models/printable-data.model';
import { findSelectedPlotter, PiDaemon, PlotterDetails, PrinterConnectionStatus, reconcilePrinterSelection } from 'app/models/printing.model';
import { Serie } from 'app/models/serie.model';
import { PlotterConfig } from 'app/models/settings.model';
import { Credentials, defaultUserConfiguration, LoginData, SessionBanner, User, UserConfiguration, UserInfo, UserPasswordChange } from 'app/models/user.model';
import { isRuler } from 'app/utils/fabric.utils';
import { appConfig } from 'environments/environment';

export enum SessionLoading {
  login = 'LOGIN',
  logout = 'LOGOUT',
  updateUser = 'UPDATE_USER',
  printing = 'PRINTING',
}

export const DEFAULT_BANNER_PICTURE = 'assets/images/banner-magic.jpg';

export interface AppStoreState {
  readonly isAppInitialized: boolean;
  readonly isUserAuthenticated: boolean;
  readonly isPrintingServerUp: boolean;
  readonly user?: User;
  readonly userConfiguration: UserConfiguration;
  readonly credentials?: Credentials;
  readonly loading: SessionLoading[];
  readonly printableDataSession: PrintableDataSession;
  readonly banner?: SessionBanner;
  readonly isTabletMode: boolean;
  readonly isSidenavOpen: boolean;
  readonly connectedPlotters: PlotterDetails[];
  readonly piDaemons: PiDaemon[];
  readonly groupedBrands?: EntitiesModel['groupedBrands'];
  readonly groupedSeries?: EntitiesModel['groupedSeries'];
  readonly groupedModels?: EntitiesModel['groupedModels'];
  readonly groupedPrintableData?: EntitiesModel['groupedPrintableData'];
}

interface AuthenticatedSession {
  generation: number;
  credentials: Credentials;
}

const createNotAccessibleStatus = (): PrinterConnectionStatus => ({
  isUp: false,
  devices: [],
});

const createInitialState = (): AppStoreState => ({
  isAppInitialized: false,
  isUserAuthenticated: false,
  isPrintingServerUp: false,
  user: undefined,
  credentials: undefined,
  loading: [],
  userConfiguration: { ...defaultUserConfiguration },
  printableDataSession: {
    initialPieces: [],
    loadedPieces: [],
    currentCanvasState: {
      zoom: AVAILABLE_ZOOM[2].value,
    },
  },
  banner: {
    picture: DEFAULT_BANNER_PICTURE,
  },
  isTabletMode: false,
  isSidenavOpen: true,
  connectedPlotters: [],
  piDaemons: [],
  groupedBrands: undefined,
  groupedSeries: undefined,
  groupedModels: undefined,
  groupedPrintableData: undefined,
});

export const AppStore = signalStore(
  { providedIn: 'root', protectedState: true },
  withState(createInitialState),
  withEntities({ entity: type<Brand>(), collection: 'brand' }),
  withEntities({ entity: type<Serie>(), collection: 'serie' }),
  withEntities({ entity: type<Model>(), collection: 'model' }),
  withEntities({ entity: type<PrintableData>(), collection: 'printableData' }),
  withProps(() => ({
    _authService: inject(AuthService),
    _appTranslateService: inject(TouchTranslateService),
    _userService: inject(UserService),
    _router: inject(Router),
    _notificationService: inject(NotificationService),
    _printableDataService: inject(PrintableDataService),
    _printingService: inject(PrintingService),
    _printingSessionService: inject(PrintingSessionService),
    _dialog: inject(MatDialog),
    _handleErrorsService: inject(HandleErrorsService),
  })),
  withComputed(store => ({
    allowedPrintedWindows: () => store.user()?.allowedWindows,
    allowedPrintedPPF: () => store.user()?.allowedPPF,
    isAllowedPrintedWindows: () => (store.user()?.allowedWindows ?? 0) > 0,
    isAllowedPrintedPPF: () => (store.user()?.allowedPPF ?? 0) > 0,
    filmWidth: () => store.userConfiguration().filmWidth,
    currentPlotter: () => store.userConfiguration().plotter,
    currentPrinter: () => findSelectedPlotter(store.connectedPlotters(), store.userConfiguration().printer),
    currentForm: (): FilterFormFields | undefined => {
      const selectedPrintableDataId = store.printableDataSession().selectedPrintableDataId;

      if (!selectedPrintableDataId) {
        return undefined;
      }

      const printableData = store.printableDataEntityMap()[selectedPrintableDataId];
      const model = store.modelEntityMap()[printableData?.modelId];
      const serie = store.serieEntityMap()[model?.serieId];
      const brand = store.brandEntityMap()[serie?.brandId];

      return {
        pieceType: printableData?.type,
        brand,
        serie,
        model,
        printableData,
      };
    },
    initialPieces: () => store.printableDataSession().initialPieces,
    loadedPieces: () => store.printableDataSession().loadedPieces,
    stagedPieces: () => differenceBy(store.printableDataSession().initialPieces, store.printableDataSession().loadedPieces, (pieces: Piece) => pieces.label),
    rulerConfig: () => store.printableDataSession().rulerConfig,
    currentCanvas: () => store.printableDataSession().currentCanvasState.stateAsString,
    currentSelection: () => store.printableDataSession().currentCanvasState.currentSelection,
    currentZoom: () => store.printableDataSession().currentCanvasState.zoom,
    currentControlEvent: () => store.printableDataSession().controlEvent,
    fabricQuery: () => ({
      fabricState: store.printableDataSession().currentCanvasState.stateAsString,
      plotterConfig: normalizePlotterConfig(store.userConfiguration().plotter),
      printableDataId: store.printableDataSession().selectedPrintableDataId,
    }),
    loginLoading: () => store.loading().includes(SessionLoading.login),
    logoutLoading: () => store.loading().includes(SessionLoading.logout),
    updateUserLoading: () => store.loading().includes(SessionLoading.updateUser),
    printInProgress: () => store.loading().includes(SessionLoading.printing),
  })),
  withComputed(store => ({
    currentPrinterName: () => store.currentPrinter()?.name || null,
    currentPrinterSelection: () => (store.currentPrinter() ? store.userConfiguration().printer || null : null),
  })),
  withMethods(store => {
    let printingSessionSubscription: Subscription | undefined;
    let sessionGeneration = 0;
    let activeSession: AuthenticatedSession | undefined;

    const beginSessionTransition = (): number => {
      activeSession = undefined;
      sessionGeneration++;
      printingSessionSubscription?.unsubscribe();
      printingSessionSubscription = undefined;
      patchState(store, state => ({ loading: state.loading.filter(loading => loading !== SessionLoading.printing) }));

      return sessionGeneration;
    };

    const isCurrentSession = (session: AuthenticatedSession): boolean =>
      activeSession === session && session.generation === sessionGeneration && credentialsMatch(store.credentials(), session.credentials);

    const activateSession = (generation: number, credentials: Credentials, authenticateBeforeUserLoad: boolean): AuthenticatedSession | undefined => {
      if (generation !== sessionGeneration) {
        return undefined;
      }

      const session = { generation, credentials };
      activeSession = session;
      patchState(store, {
        credentials,
        isUserAuthenticated: authenticateBeforeUserLoad || store.isUserAuthenticated(),
        user: undefined,
        banner: { picture: DEFAULT_BANNER_PICTURE },
      });

      return session;
    };

    const addLoading = (loadingType: SessionLoading): void => {
      patchState(store, state => ({
        loading: state.loading.includes(loadingType) ? state.loading : [...state.loading, loadingType],
      }));
    };

    const removeLoading = (loadingType: SessionLoading): void => {
      patchState(store, state => ({
        loading: state.loading.filter(loading => loading !== loadingType),
      }));
    };

    const setUser = (user: User): void => {
      patchState(store, { user });
      store._appTranslateService.updateUserLanguage(user.language);
    };

    const setUserConfiguration = (userConfiguration: UserConfiguration): void => {
      patchState(store, state => ({
        userConfiguration: {
          ...state.userConfiguration,
          ...userConfiguration,
        },
      }));
      store._userService.setUserConfigurationToStorage(store.userConfiguration());
    };

    const setPrintingCandidates = (plotters: PlotterDetails[]): void => {
      const currentSelection = store.userConfiguration().printer;
      const nextSelection = reconcilePrinterSelection(plotters, currentSelection);

      patchState(store, {
        connectedPlotters: plotters,
      });

      if (nextSelection && nextSelection !== currentSelection) {
        setUserConfiguration({ printer: nextSelection });
      }
    };

    const setPrintingSessionStatus = (status: PrinterConnectionStatus): void => {
      patchState(store, { isPrintingServerUp: status.isUp, piDaemons: status.daemons ?? [] });
      setPrintingCandidates(status.devices);
    };

    const stopPrintingSessionPoll = (resetStatus = true): void => {
      printingSessionSubscription?.unsubscribe();
      printingSessionSubscription = undefined;

      if (resetStatus) {
        setPrintingSessionStatus(createNotAccessibleStatus());
      }
    };

    const startPrintingSessionPoll = (session: AuthenticatedSession): void => {
      stopPrintingSessionPoll(false);
      printingSessionSubscription = store._printingSessionService
        .watch(status => {
          if (isCurrentSession(session)) {
            setPrintingSessionStatus(status);
          }
        })
        .subscribe();
    };

    const cleanupDevicePings = (): void => {
      stopPrintingSessionPoll();
    };

    const finishAppInitialization = (): void => {
      patchState(store, { isAppInitialized: true });
    };

    const loadBanner = async (session: AuthenticatedSession): Promise<void> => {
      if (!isCurrentSession(session)) {
        return;
      }

      patchState(store, { banner: { picture: DEFAULT_BANNER_PICTURE } });

      try {
        const banner = await firstValueFrom(store._userService.getBanner());
        if (typeof banner?.picture === 'string' && banner.picture.trim() && isCurrentSession(session)) {
          patchState(store, {
            banner: { picture: `${appConfig.apiHost}/uploads/banner/${banner.picture}` },
          });
        }
      } catch (error) {
        if (isCurrentSession(session)) {
          store._handleErrorsService.handleErrors(error);
        }
      }
    };

    const loadUser = async (session: AuthenticatedSession, getBanner = false): Promise<boolean> => {
      const user = await firstValueFrom(store._userService.getUser(session.credentials.payload.id));
      if (!isCurrentSession(session)) {
        return false;
      }

      setUser(user);

      if (getBanner) {
        await loadBanner(session);
      }

      return isCurrentSession(session);
    };

    const loadAvailablePlotterBrands = async (session: AuthenticatedSession): Promise<void> => {
      const plotterBrands = await firstValueFrom(store._printingService.getAvailablePlotters());
      if (!isCurrentSession(session)) {
        return;
      }

      setPrintingCandidates(plotterBrands);
      startPrintingSessionPoll(session);
    };

    const loadEntities = async (session: AuthenticatedSession): Promise<void> => {
      const entities = await firstValueFrom(store._printableDataService.loadEntities());
      if (!isCurrentSession(session)) {
        return;
      }

      patchState(
        store,
        setAllEntities(entities.brands, { collection: 'brand', selectId: brand => brand.id as number }),
        setAllEntities(entities.series, { collection: 'serie', selectId: serie => serie.id as number }),
        setAllEntities(entities.models, { collection: 'model', selectId: model => model.id as number }),
        setAllEntities(entities.printableData, { collection: 'printableData', selectId: printableData => printableData.id as number }),
        {
          groupedBrands: entities.groupedBrands,
          groupedSeries: entities.groupedSeries,
          groupedModels: entities.groupedModels,
          groupedPrintableData: entities.groupedPrintableData,
        },
      );
    };

    const completeAuthenticatedSession = async (session: AuthenticatedSession, getBanner: boolean, finishInit = false): Promise<void> => {
      if (!isCurrentSession(session)) {
        return;
      }

      void loadEntities(session).catch(() => undefined);
      void loadAvailablePlotterBrands(session).catch(() => undefined);

      try {
        const userLoaded = await loadUser(session, getBanner);
        if (userLoaded && isCurrentSession(session)) {
          patchState(store, { isUserAuthenticated: true });
        }
      } finally {
        if (finishInit && isCurrentSession(session)) {
          finishAppInitialization();
        }
      }
    };

    const loadInitialPieces = async (selectedPrintableDataId: number): Promise<void> => {
      const printableData = await firstValueFrom(store._printableDataService.loadPrintableData(selectedPrintableDataId));
      patchState(store, state => ({
        printableDataSession: {
          ...state.printableDataSession,
          initialPieces: (printableData.pieces ?? []).filter((piece: Piece) => !piece.label.includes('noname')),
        },
      }));
    };

    const runPrint = async (session: AuthenticatedSession): Promise<void> => {
      const currentPrinter = store.currentPrinter();

      try {
        if (!currentPrinter) {
          throw { message: 'device_not_found' };
        }

        const fabricQuery = store.fabricQuery();
        const status = await firstValueFrom(
          store._printingService.print({ ...fabricQuery, fabricState: removeRulersFromFabricState(fabricQuery.fabricState) }, currentPrinter.target),
        );
        if (!isCurrentSession(session)) {
          return;
        }
        if (status !== 'SUCCESS' && status !== 'FAIL') {
          throw new Error('invalid print status');
        }

        const succeeded = status === 'SUCCESS';
        store._notificationService.notify(succeeded ? NotificationType.PRINT_SUCCESS : NotificationType.PRINT_FAILED, succeeded ? 5000 : 7000);
      } catch {
        if (isCurrentSession(session)) {
          store._notificationService.notify(NotificationType.PRINT_FAILED, 7000);
        }
      }

      if (isCurrentSession(session)) {
        void loadUser(session, false).catch(() => undefined);
      }
    };

    return {
      async initializeApp(): Promise<void> {
        const generation = beginSessionTransition();
        patchState(store, { banner: { picture: DEFAULT_BANNER_PICTURE } });

        try {
          await firstValueFrom(store._appTranslateService.initLanguage());
        } catch {
          // Initialization should continue even if language files are unavailable.
        }

        if (generation !== sessionGeneration) {
          return;
        }

        setUserConfiguration(store._userService.getUserConfigurationFromStorage());

        const credentials = store._authService.checkCredentials();
        if (isEmpty(credentials)) {
          patchState(store, {
            credentials: undefined,
            isUserAuthenticated: false,
            user: undefined,
          });
          finishAppInitialization();

          return;
        }

        const session = activateSession(generation, credentials, true);
        if (!session) {
          return;
        }

        try {
          await completeAuthenticatedSession(session, true, true);
        } catch {
          if (isCurrentSession(session)) {
            finishAppInitialization();
          }
        }
      },

      async login(loginData: LoginData): Promise<void> {
        const generation = beginSessionTransition();
        patchState(store, { banner: { picture: DEFAULT_BANNER_PICTURE } });
        addLoading(SessionLoading.login);

        try {
          const credentials = await firstValueFrom(store._authService.login(loginData));
          if (generation !== sessionGeneration) {
            return;
          }

          const session = activateSession(generation, credentials, true);
          if (!session) {
            return;
          }

          store._authService.setCredentialsToStorage(credentials, loginData);
          removeLoading(SessionLoading.login);
          void store._router.navigate(['/']);
          void completeAuthenticatedSession(session, true).catch(() => undefined);
          store._notificationService.notify(NotificationType.LOGIN_SUCCESS, 5000);
        } catch (err) {
          if (generation === sessionGeneration) {
            removeLoading(SessionLoading.login);
            store._handleErrorsService.handleErrors(err, 'login');
          }
        }
      },

      setCredentials(credentials: Credentials): void {
        const currentSession = activeSession;
        const generation = ++sessionGeneration;

        // Token rotation advances the generation while preserving the logical tenant lease that caused the refresh.
        if (currentSession && samePrincipal(currentSession.credentials, credentials)) {
          currentSession.credentials = credentials;
          currentSession.generation = generation;
        } else {
          activeSession = { generation, credentials };
          patchState(store, {
            user: undefined,
            banner: { picture: DEFAULT_BANNER_PICTURE },
          });
        }

        patchState(store, {
          credentials,
          isUserAuthenticated: true,
        });
      },

      logout(): void {
        beginSessionTransition();
        addLoading(SessionLoading.logout);
        cleanupDevicePings();
        store._authService.clearStorage();
        patchState(store, state => ({
          ...createInitialState(),
          userConfiguration: state.userConfiguration,
        }));
        store._dialog.closeAll();
        store._notificationService.notify(NotificationType.LOGOUT, 5000);
        void store._router.navigate(['/login']);
      },

      refreshUserData(): void {
        const userId = store.user()?.id;
        const session = activeSession;

        if (!session || userId !== session.credentials.payload.id) {
          return;
        }

        void loadUser(session, false).catch(() => undefined);
      },

      updateUserConfiguration(partialUserConfiguration: Partial<UserConfiguration>): void {
        setUserConfiguration(partialUserConfiguration);
      },

      updateUser(userInfo: UserInfo): void {
        const currentUser = store.user();

        if (!currentUser) {
          return;
        }

        addLoading(SessionLoading.updateUser);
        void firstValueFrom(store._userService.updateUser({ ...userInfo, id: currentUser.id }))
          .then(user => {
            setUser(user);
            store._notificationService.notify(NotificationType.PROFILE_UPDATE_SUCCESS, 7000);
          })
          .finally(() => removeLoading(SessionLoading.updateUser));
      },

      updateUserPassword(passwords: UserPasswordChange): void {
        addLoading(SessionLoading.updateUser);
        void firstValueFrom(store._userService.changePassword(passwords))
          .then(() => {
            store._notificationService.notify(NotificationType.PASSWORD_CHANGE_SUCCESS, 7000);
          })
          .catch(error => {
            const apiError = typeof error === 'object' && error !== null && 'error' in error ? error.error : undefined;
            const code = typeof apiError === 'object' && apiError !== null && 'error' in apiError ? apiError.error : undefined;

            if (code === 'PASSWORD_UPDATE_FAILED') {
              store._notificationService.notify(NotificationType.INCORRECT_PASSWORD, 7000);
            } else {
              store._handleErrorsService.handleErrors(error);
            }
          })
          .finally(() => removeLoading(SessionLoading.updateUser));
      },

      setIsTabletMode(isTabletMode: boolean): void {
        patchState(store, { isTabletMode });
      },

      setIsSidenavOpen(isSidenavOpen: boolean): void {
        patchState(store, { isSidenavOpen });
      },

      setSelectedPrintableData(selectedPrintableDataId?: number): void {
        patchState(store, state => ({
          printableDataSession: {
            rulerConfig: state.printableDataSession.rulerConfig,
            selectedPrintableDataId,
            initialPieces: [],
            loadedPieces: state.printableDataSession.loadedPieces.length > 0 ? [] : state.printableDataSession.loadedPieces,
            currentCanvasState: {
              stateAsString: '',
              zoom: state.printableDataSession.currentCanvasState.zoom,
            },
          },
        }));

        if (selectedPrintableDataId != null) {
          void loadInitialPieces(selectedPrintableDataId);
        }
      },

      setInitialPieces(initialPieces: Piece[]): void {
        patchState(store, state => ({
          printableDataSession: {
            ...state.printableDataSession,
            initialPieces: cloneDeep(initialPieces),
          },
        }));
      },

      loadPieces(piecesToLoad: Piece[]): void {
        patchState(store, state => ({
          printableDataSession: {
            ...state.printableDataSession,
            loadedPieces: cloneDeep([...state.printableDataSession.loadedPieces, ...piecesToLoad]),
          },
        }));
      },

      loadPiecesFromPreview(piecesToLoad: Piece[]): void {
        patchState(store, state => ({
          printableDataSession: {
            ...state.printableDataSession,
            loadedPieces: cloneDeep(piecesToLoad),
          },
        }));
      },

      syncPiecesFromCanvas(piecesToSync: string[]): void {
        patchState(store, state => ({
          printableDataSession: {
            ...state.printableDataSession,
            loadedPieces: state.printableDataSession.initialPieces.filter(piece => piecesToSync.includes(piece.label)),
          },
        }));
      },

      stagePieces(piecesToStage: Piece[]): void {
        patchState(store, state => ({
          printableDataSession: {
            ...state.printableDataSession,
            loadedPieces: differenceBy(state.printableDataSession.loadedPieces, piecesToStage, (pieces: Piece) => pieces.label),
          },
        }));
      },

      updateRulerConfig(rulerConfig: PrintableDataSession['rulerConfig']): void {
        patchState(store, state => ({
          printableDataSession: {
            ...state.printableDataSession,
            rulerConfig,
          },
        }));
      },

      updateCurrentControlEvent(controlEvent: PrintableDataSession['controlEvent']): void {
        patchState(store, state => ({
          printableDataSession: {
            ...state.printableDataSession,
            controlEvent,
          },
        }));
      },

      updateCurrentCanvasState(currentCanvasState: Partial<PrintableDataSession['currentCanvasState']>): void {
        patchState(store, state => ({
          printableDataSession: {
            ...state.printableDataSession,
            currentCanvasState: {
              ...state.printableDataSession.currentCanvasState,
              ...currentCanvasState,
            },
          },
        }));
      },

      startPrinting(): void {
        if (store.printInProgress()) {
          return;
        }

        const session = activeSession;
        if (!session) {
          return;
        }

        addLoading(SessionLoading.printing);
        void runPrint(session).finally(() => {
          if (isCurrentSession(session)) {
            removeLoading(SessionLoading.printing);
          }
        });
      },

      refreshPrintingCandidates(): void {
        const session = activeSession;
        if (session) {
          void loadAvailablePlotterBrands(session).catch(() => undefined);
        }
      },

      _cleanupDevicePings: cleanupDevicePings,
    };
  }),
  withHooks({
    onDestroy(store): void {
      store._cleanupDevicePings();
    },
  }),
);

function normalizePlotterConfig(plotter: string | undefined): PlotterConfig {
  switch (plotter) {
    case 'PNN':
      return { unit: 0.0255, switchAxis: false, mirror: true };
    case 'PR':
      return { unit: 0.025, switchAxis: true, mirror: false };
    case 'PN':
    case undefined:
    default:
      return { unit: 0.025, switchAxis: false, mirror: true };
  }
}

function removeRulersFromFabricState(fabricState: string | undefined): string {
  const state: FabricJSON = JSON.parse(fabricState);
  state.objects = state.objects.filter(obj => !isRuler(obj));

  return JSON.stringify(state);
}

function credentialsMatch(left: Credentials | undefined, right: Credentials): boolean {
  return (
    left?.token === right.token && left.refreshToken === right.refreshToken && left.payload.id === right.payload.id && left.payload.role === right.payload.role
  );
}

function samePrincipal(left: Credentials, right: Credentials): boolean {
  return left.payload.id === right.payload.id && left.payload.role === right.payload.role;
}
